import React from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

const CreateCustomerModal = ({
  show,
  onClose,
  loading,
  customerFormData,
  setCustomerFormData,
  industryOptions,
  employeeConfig,
  handleCompanySizeChange,
  createCustomer,
  resetCustomerForm
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-2 sm:px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl transform transition-all my-4 sm:my-8 sm:align-middle w-full max-w-lg sm:max-w-2xl md:max-w-4xl">
          <div className="px-3 sm:px-6 pt-5 pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Add New CRM Lead</h3>
              <button
                onClick={onClose}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); createCustomer(); }} className="space-y-6">
              {/* Company Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Company Name * <span className="text-xs text-gray-500 dark:text-gray-400">(2-200 characters)</span>
                  </label>
                  <input
                    type="text"
                    required
                    minLength={2}
                    maxLength={200}
                    value={customerFormData.company_name}
                    onChange={(e) => setCustomerFormData({...customerFormData, company_name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                    placeholder="Enter company name"
                  />
                  {customerFormData.company_name && (customerFormData.company_name.length < 2 || customerFormData.company_name.length > 200) && (
                    <p className="text-xs text-red-600 mt-1">Company name must be between 2 and 200 characters</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Industry *
                  </label>
                  <select
                    required
                    value={customerFormData.industry}
                    onChange={(e) => setCustomerFormData({...customerFormData, industry: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                  >
                    {industryOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Company Size *
                  </label>
                  <select
                    required
                    value={customerFormData.company_size || ''}
                    onChange={(e) => handleCompanySizeChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                  >
                    <option value="">Select Company Size</option>
                    {employeeConfig.length === 0 ? (
                      <option value="" disabled>Loading...</option>
                    ) : (
                      employeeConfig.map((config) => (
                        <option key={config._id} value={config.name}>
                          {config.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Price
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      value={customerFormData.price}
                      onChange={(e) => setCustomerFormData({...customerFormData, price: e.target.value})}
                      className="w-full pl-7 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Lead Source
                  </label>
                  <select
                    value={customerFormData.lead_source}
                    onChange={(e) => setCustomerFormData({...customerFormData, lead_source: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                  >
                    <option value="">Select source</option>
                    <option value="Website">Website</option>
                    <option value="Referral">Referral</option>
                    <option value="Cold Call">Cold Call</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="Conference">Conference</option>
                    <option value="Partner">Partner</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Lead Status *
                  </label>
                  <select
                    required
                    value={customerFormData.status}
                    onChange={(e) => setCustomerFormData({...customerFormData, status: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                  >
                    <option value="New Lead">New Lead</option>
                    <option value="Contacted">Contacted</option>
                    <option value="Qualified">Qualified</option>
                    <option value="Proposal Sent">Proposal Sent</option>
                    <option value="Negotiation">Negotiation</option>
                    <option value="Lost">Lost</option>
                  </select>
                </div>
              </div>

              {/* Primary Contact Details */}
              <div className="border-t pt-4">
                <h4 className="text-lg font-semibold mb-4">Primary Contact</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Full Name * <span className="text-xs text-gray-500 dark:text-gray-400">(2-200 characters)</span>
                    </label>
                    <input
                      type="text"
                      required
                      minLength={2}
                      maxLength={200}
                      value={customerFormData.primary_contact_name}
                      onChange={(e) => setCustomerFormData({...customerFormData, primary_contact_name: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                      placeholder="John Doe"
                    />
                    {customerFormData.primary_contact_name && (customerFormData.primary_contact_name.length < 2 || customerFormData.primary_contact_name.length > 200) && (
                      <p className="text-xs text-red-600 mt-1">Name must be between 2 and 200 characters</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Email * <span className="text-xs text-gray-500 dark:text-gray-400">(Valid email format)</span>
                    </label>
                    <input
                      type="email"
                      required
                      maxLength={254}
                      pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                      value={customerFormData.primary_contact_email}
                      onChange={(e) => setCustomerFormData({...customerFormData, primary_contact_email: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                      placeholder="contact@company.com"
                    />
                    {customerFormData.primary_contact_email && !/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(customerFormData.primary_contact_email) && (
                      <p className="text-xs text-red-600 mt-1">Please enter a valid email address</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Phone * <span className="text-xs text-gray-500 dark:text-gray-400">(10-15 digits)</span>
                    </label>
                    <input
                      type="tel"
                      required
                      minLength={10}
                      maxLength={15}
                      pattern="[0-9+\-\s()]*"
                      value={customerFormData.primary_contact_phone}
                      onChange={(e) => setCustomerFormData({...customerFormData, primary_contact_phone: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                      placeholder="+1 (555) 123-4567"
                    />
                    {customerFormData.primary_contact_phone && (customerFormData.primary_contact_phone.replace(/[^0-9]/g, '').length < 10 || customerFormData.primary_contact_phone.length > 15) && (
                      <p className="text-xs text-red-600 mt-1">Phone must contain 10-15 digits</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Job Title <span className="text-xs text-gray-500 dark:text-gray-400">(Max 100 characters)</span>
                    </label>
                    <input
                      type="text"
                      maxLength={100}
                      value={customerFormData.primary_contact_title}
                      onChange={(e) => setCustomerFormData({...customerFormData, primary_contact_title: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                      placeholder="CEO, CTO, etc."
                    />
                    {customerFormData.primary_contact_title && customerFormData.primary_contact_title.length > 100 && (
                      <p className="text-xs text-red-600 mt-1">Job title must be less than 100 characters</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes and Additional Info */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  Notes <span className="text-xs text-gray-500 dark:text-gray-400">(Max 1000 characters)</span>
                </label>
                <textarea
                  value={customerFormData.notes}
                  onChange={(e) => setCustomerFormData({...customerFormData, notes: e.target.value})}
                  maxLength={1000}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                  placeholder="Any additional notes about this lead..."
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{customerFormData.notes.length}/1000 characters</span>
                  {customerFormData.notes.length > 1000 && (
                    <span className="text-xs text-red-600">Notes exceed maximum length</span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    resetCustomerForm();
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create Lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCustomerModal;
